import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";
import { TPlatformAdmin, TRole } from "../types";

type SaveCallbacks = { onSuccess?: (res: any) => void; onError?: (err: any) => void };

export const useAdminList = (qData: { key?: string; page?: number; limit?: number } = {}) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.ADMIN, qData],
    queryFn: () => client.get(API_ENDPOINTS.ADMIN, { params: qData }),
    select: (res: any) => res.data.data as TPlatformAdmin[],
    refetchOnWindowFocus: false,
  });
};

export const useAdminCreate = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; email: string; password: string; role: string }) =>
      client.post(API_ENDPOINTS.ADMIN, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ADMIN] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useAdminToggleActive = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      client.patch(API_ENDPOINTS.ADMIN_TOGGLE_ACTIVE(id), { is_active }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ADMIN] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useRolesByCompany = (companyId: number | null) => {
  return useQuery({
    queryKey: ["roles", companyId],
    queryFn: () => client.get(API_ENDPOINTS.ROLES_BY_COMPANY(companyId!)),
    select: (res: any) => res.data.data as TRole[],
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });
};
